import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useFeedBack } from "../../../context/FeedBackContext";
import useAxios from "../../../hooks/useAxios";
import useOrders from "../../../hooks/useOrders";
import useOrdersTypes from "../../../hooks/useOrdersTypes";
import useServices from "../../../hooks/useServices";
import useZones from "../../../hooks/useZones";
import useOrderStatuses from "../../../hooks/useOrderStatuses";
import useCategories from "../../../hooks/useCategories";
import profile from "../../../images/profile.png";
import { safeFormatCLP } from "../../../util/format";
import ActionDropdown from "../../../components/ActionDropdown";
import { ProgressBar } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaList, FaThLarge, FaEye, FaFilter } from "react-icons/fa";

const getTypeText = (typeString) => {
    if (!typeString || typeString === '--') return <span className="text-muted">--</span>;
    const lower = String(typeString).toLowerCase();
    let textClass = "text-secondary";
    let customStyle = {};

    if (lower.includes("mensual") || lower.includes("monthly")) {
        textClass = "text-success";
    } else if (lower.includes("extraordinario") || lower.includes("extraordinary")) {
        textClass = "text-primary";
    } else if (lower.includes("manual")) {
        textClass = "text-warning";
        customStyle = { color: '#d97706' };
    }

    return (
        <span
            className={`fw-bold ${textClass} text-truncate d-inline-block`}
            style={{ maxWidth: '140px', height: 11, ...customStyle }}
            title={typeString}
        >
            {typeString}
        </span>
    );
};

const Ejemplo2 = () => {
    const { permissions } = useAuth();
    const { setCustomAlert, setLoading } = useFeedBack();

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const getInitialFilters = () => {
        return {
            page: parseInt(searchParams.get('page')) || 1,
            serviceIds: searchParams.get('serviceIds') || '',
            zoneId: searchParams.get('zoneId') || '',
            start: searchParams.get('start') || '',
            end: searchParams.get('end') || '',
            orderTypeId: searchParams.get('orderTypeId') || '',
            categoryId: searchParams.get('categoryId') || '',
            id: searchParams.get('id') || '',
            orderStatusCode: searchParams.get('orderStatusCode') || ''
        };
    };

    const initialFilters = getInitialFilters();
    const [filters, setFilters] = useState(initialFilters);
    const hasInitialActiveFilters = Object.entries(initialFilters).some(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined);
    const [showFilters, setShowFilters] = useState(hasInitialActiveFilters);
    const activeFiltersCount = Object.entries(filters).filter(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined).length;
    const [servicesFilters] = useState({ currentUserServices: 'true', perPage: 500 });
    const [selectedValues, setSelectedValues] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [expandedIds, setExpandedIds] = useState([]);

    const [{ services }] = useServices({ params: { ...servicesFilters } }, { useCache: false });
    const [{ zones }] = useZones({ params: { ...servicesFilters } }, { useCache: false });
    const [{ categories }] = useCategories({ params: { orderBy: 'path,asc' } }, { useCache: false });
    const [{ ordersTypes }] = useOrdersTypes({ options: { useCache: false } });
    const [{ orderStatuses }] = useOrderStatuses({ options: { useCache: false } });
    const [{ orders, total, numberOfPages, error: ordersError, loading }, getOrders] = useOrders({ params: { ...filters }, options: { useCache: false } });
    const [{ error: deleteError, loading: deleteLoading }, deleteProvider] = useAxios({ method: 'DELETE' }, { manual: true, useCache: false });

    useEffect(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '') {
                params.set(key, value.toString());
            }
        });
        setSearchParams(params);
    }, [filters, setSearchParams]);

    useEffect(() => {
        getOrders();
    }, [filters]);

    useEffect(() => {
        setLoading?.({ show: deleteLoading, message: 'Eliminando Pedidos' });
    }, [deleteLoading]);

    const toggleExpand = (id) => {
        setExpandedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleDelete = (value) => {
        deleteProvider({ url: `/orders/${value?.id}` }).then(() => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'El pedido ha sido eliminado exitosamente.',
                show: true
            });
            getOrders();
        });
    };

    const handleSelectValue = (selectedValue) => {
        const value = selectedValues?.includes(Number(selectedValue?.id));
        if (value) {
            setSelectedValues(selectedValues?.filter(n => n !== Number(selectedValue?.id)));
        } else {
            setSelectedValues([...selectedValues, Number(selectedValue?.id)]);
        }
    };

    const handleChange = (e) => {
        setFilters(old => ({
            ...old,
            [e.target.name]: e.target.value,
            page: 1
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            page: 1, serviceIds: '', zoneId: '', start: '', end: '',
            orderTypeId: '', categoryId: '', id: '', orderStatusCode: ''
        });
    };

    const handlePageChange = (page) => {
        setFilters(old => ({ ...old, page: page }));
    };

    const handlePerPageChange = (e) => {
        setFilters(old => ({ ...old, [e.target.name]: e.target.value, page: 1 }));
    };

    return (
        <div>
            {/* Selector de Modo de Vista & Filtros */}
            <div className="card p-3 mb-4 shadow-sm border">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold me-2">Vista:</span>
                        <div className="btn-group" role="group">
                            <Link to="/pedidos" className="btn btn-sm btn-outline-primary">
                                Tabla
                            </Link>
                            <Link to="/pedidos-grilla" className="btn btn-sm btn-outline-primary">
                                Grilla
                            </Link>
                            <Link to="/pedidos-lista" className="btn btn-sm btn-primary active">
                                Lista
                            </Link>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FaFilter />
                            <span>Filtros</span>
                            {activeFiltersCount > 0 && (
                                <span className="badge bg-warning text-dark ms-1">{activeFiltersCount}</span>
                            )}
                            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                        {activeFiltersCount > 0 && (
                            <button className="btn btn-sm btn-outline-danger" onClick={handleClearFilters}>
                                Limpiar Filtros
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros de Búsqueda Desplegables */}
            {showFilters && (
                <div className="card mb-4 p-3 shadow-sm border">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-primary">Filtros de Búsqueda</h5>
                                <button className="btn btn-outline-secondary btn-sm" onClick={handleClearFilters}>
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>

                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Número de pedido</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={filters?.id}
                                    onChange={handleChange}
                                    name="id"
                                    placeholder="Número de pedido..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Servicio</label>
                                <select name="serviceIds" className="form-control form-control-sm" value={filters?.serviceIds} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {services?.map((service, i) => (
                                        <option key={i} value={service?.id}>{service?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Zonas</label>
                                <select name="zoneId" className="form-control form-control-sm" value={filters?.zoneId} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {zones?.map((zone, i) => (
                                        <option key={i} value={zone?.id}>{zone?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Categoría</label>
                                <select name="categoryId" className="form-control form-control-sm" value={filters?.categoryId} onChange={handleChange}>
                                    <option value="">Todas</option>
                                    {categories?.map((cat, i) => (
                                        <option key={i} value={cat?.id}>{cat?.path || cat?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Listado Compacto Acordeón (Sin espacio, separado por líneas) */}
            {loading ? (
                <div className="text-center p-5 card">
                    <h5>Cargando pedidos...</h5>
                </div>
            ) : orders?.length > 0 ? (
                <>
                    <div className="card shadow-sm border overflow-hidden">
                        <div className="list-group list-group-flush">
                            {orders.map((order) => {
                                const isExpanded = expandedIds.includes(order.id);
                                const isSelected = selectedValues.includes(Number(order.id));

                                return (
                                    <div key={order.id} className={`list-group-item p-0 ${isSelected ? 'active-row' : ''}`}>
                                        {/* Fila Principal (Siempre Visible) */}
                                        <div className="p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            {/* Selección + ID */}
                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: '110px' }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectValue(order)}
                                                />
                                                <span className="badge bg-primary fs-6">#{order.id}</span>
                                            </div>

                                            {/* Solicitante */}
                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: '200px', flex: 1 }}>
                                                <img
                                                    src={order.image_path || order.user_image_path || profile}
                                                    alt={order.user_name}
                                                    className="rounded-circle border"
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                    onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                                                />
                                                <div className="overflow-hidden">
                                                    <div className="fw-bold text-truncate" title={order.user_name}>
                                                        {order.user_name || '--'}
                                                    </div>
                                                    <small className="text-muted d-block">{order.created_at || '--'}</small>
                                                </div>
                                            </div>

                                            {/* Tipo y Servicio */}
                                            <div className="d-flex flex-column" style={{ minWidth: '180px', flex: 1 }}>
                                                <div>
                                                    <small className="text-muted me-1">Tipo:</small>
                                                    <small>
                                                        {getTypeText(order.order_type || order.type)}
                                                    </small>
                                                </div>
                                                <div className="text-truncate small fw-semibold text-muted" title={order.service_name}>
                                                    {order.service_name || '--'}
                                                </div>
                                            </div>

                                            {/* Monto */}
                                            <div className="text-end" style={{ minWidth: '120px' }}>
                                                <small className="text-muted d-block">Monto:</small>
                                                <span className="fw-bold text-success fs-6">
                                                    {safeFormatCLP(Number(order.total))}
                                                </span>
                                            </div>

                                            {/* Estatus y Progreso */}
                                            <div style={{ minWidth: '160px', flex: 1 }}>
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span className="text-capitalize fw-semibold" style={{ color: order.status_color || '#333' }}>
                                                        {order.order_status || order.status_name || '--'}
                                                    </span>
                                                    <span>{order.progress || 0}%</span>
                                                </div>
                                                <ProgressBar
                                                    now={order.progress || 0}
                                                    variant={order.status_variant_color || 'info'}
                                                    style={{ height: '6px' }}
                                                />
                                            </div>

                                            {/* Acciones y Botón de Despliegue */}
                                            <div className="d-flex align-items-center gap-2">
                                                <ActionDropdown id={order.id} entity="orders" onDelete={() => handleDelete(order)} />
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => toggleExpand(order.id)}
                                                    title={isExpanded ? 'Ocultar detalles' : 'Ver más detalles'}
                                                >
                                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Panel Desplegable (Detalles Secundarios) */}
                                        {isExpanded && (
                                            <div className="p-3 border-top">
                                                <div className="row g-3">
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Aprobador (Jefe Servicio):</small>
                                                        <span className="fw-semibold">{order.order_boss_name || order.approver || '--'}</span>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Encargado de Adquisiciones:</small>
                                                        <span className="fw-semibold">{order.acquisition_user || '--'}</span>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Fecha de Aprobación:</small>
                                                        <span className="fw-semibold">{order.approved_at || '--'}</span>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Fecha de Envío:</small>
                                                        <span className="fw-semibold">{order.sent_at || '--'}</span>
                                                    </div>
                                                </div>
                                                <div className="mt-3 text-end">
                                                    <Link to={`/pedidos/detalles/${order.id}`} className="btn btn-sm btn-outline-primary">
                                                        <FaEye className="me-1" /> Ir al Detalle Completo
                                                    </Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Paginación Limpia */}
                    <div className="d-sm-flex text-center justify-content-between align-items-center mt-3 p-3 card shadow-sm border">
                        <div className="dataTables_info text-start">
                            <span className="text-muted small">Total de registros: <strong>{total || 0}</strong></span>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <small className="text-muted">Mostrar de:</small>
                                <select
                                    name="perPage"
                                    className="form-select form-select-sm border-secondary-subtle"
                                    style={{ width: '80px', cursor: 'pointer' }}
                                    onChange={handlePerPageChange}
                                    value={filters?.perPage || 10}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0" style={{ maxWidth: '70%' }}>
                            <button
                                className="btn btn-sm btn-light border-0 text-primary fw-bold px-3 me-1"
                                disabled={filters?.page <= 1}
                                onClick={() => filters?.page > 1 && handlePageChange(filters?.page - 1)}
                            >
                                &laquo; Anterior
                            </button>
                            <span className="p-1 d-flex align-items-center custom-horizontal-scrollbar scrollbar-primary" style={{ maxWidth: '54%', overflowX: 'auto' }}>
                                {Array.from(Array(numberOfPages || 1).keys()).map((number, i) => (
                                    <div
                                        key={i}
                                        className={`d-flex align-items-center justify-content-center fw-bold ${filters?.page === (i + 1) ? "bg-primary text-white shadow-sm" : "bg-light text-dark"}`}
                                        onClick={() => handlePageChange(1 + number)}
                                        style={{
                                            minWidth: '34px',
                                            height: '34px',
                                            borderRadius: '8px',
                                            marginRight: '6px',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {1 + number}
                                    </div>
                                ))}
                            </span>
                            <button
                                className="btn btn-sm btn-light border-0 text-primary fw-bold px-3 ms-1"
                                disabled={filters?.page >= numberOfPages}
                                onClick={() => filters?.page < numberOfPages && handlePageChange(filters?.page + 1)}
                            >
                                Siguiente &raquo;
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="card p-5 text-center">
                    <h5>No se encontraron pedidos.</h5>
                </div>
            )}
        </div>
    );
};

export default Ejemplo2;
